export interface MediumPost {
  id: string;
  title: string;
  link: string;
  author: string;
  categories: string[];
  pubDate: string;
  thumbnail?: string;
  description: string;
}

interface Rss2JsonItem {
  title: string;
  link: string;
  pubDate: string;
  author: string;
  categories: string[];
  thumbnail?: string;
  description: string;
  content: string;
  guid: string;
}

interface Rss2JsonResponse {
  status: string;
  items: Rss2JsonItem[];
}

const RSS_ENDPOINT = "https://api.rss2json.com/v1/api.json";
const RSS_FALLBACK_ENDPOINTS = [
  "https://r.jina.ai/http://medium.com/feed/@",
  "https://api.allorigins.win/raw?url=https://medium.com/feed/@",
] as const;
const LOCAL_PROXY =
  (import.meta as any).env?.VITE_MEDIUM_PROXY_URL || "/api/medium";

const extractFirstImage = (content: string, fallback?: string) => {
  if (!content) return fallback;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const img = doc.querySelector("img");
    return img?.getAttribute("src") ?? fallback;
  } catch {
    return fallback;
  }
};

const stripHtml = (content: string, limit = 200) => {
  if (!content) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = content;
  const text = tmp.textContent || tmp.innerText || "";
  if (text.length <= limit) return text;
  return text.slice(0, limit).trimEnd() + "…";
};

const normalizeRssXml = (raw: string) => {
  if (!raw) return raw;
  const xmlIndex = raw.indexOf("<?xml");
  if (xmlIndex !== -1) {
    return raw.slice(xmlIndex);
  }
  const rssIndex = raw.indexOf("<rss");
  if (rssIndex !== -1) {
    return raw.slice(rssIndex);
  }
  return raw;
};

const parseRssFallback = (xml: string): MediumPost[] => {
  if (!xml) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "text/xml");
  const items = Array.from(doc.querySelectorAll("item"));
  return items.map((item) => {
    const title = item.querySelector("title")?.textContent?.trim() || "";
    const link = item.querySelector("link")?.textContent?.trim() || "";
    const pubDate = item.querySelector("pubDate")?.textContent?.trim() || "";
    const guid = item.querySelector("guid")?.textContent?.trim() || link;
    const author =
      item.getElementsByTagName("dc:creator")[0]?.textContent?.trim() ||
      item.querySelector("author")?.textContent?.trim() ||
      "";
    const categories = Array.from(item.querySelectorAll("category"))
      .map((el) => el.textContent?.trim())
      .filter(Boolean) as string[];
    const content =
      item.getElementsByTagName("content:encoded")[0]?.textContent?.trim() ||
      item.querySelector("description")?.textContent?.trim() ||
      "";
    const thumbnail = extractFirstImage(content);
    return {
      id: guid || link,
      title,
      link,
      author,
      categories,
      pubDate,
      thumbnail,
      description: stripHtml(content),
    };
  }).filter((post) => post.title && post.link);
};

export async function fetchMediumPosts(username: string, signal?: AbortSignal): Promise<MediumPost[]> {
  const rssUrl = `https://medium.com/feed/@${username}`;
  const url = `${RSS_ENDPOINT}?rss_url=${encodeURIComponent(rssUrl)}`;
  const proxyUrl = `${LOCAL_PROXY}?username=${encodeURIComponent(username)}`;

  // Try same-origin proxy first (best reliability in production).
  try {
    const proxyRes = await fetch(proxyUrl, { signal });
    if (proxyRes.ok) {
      const rawXml = await proxyRes.text();
      const items = parseRssFallback(normalizeRssXml(rawXml));
      if (items.length) return items;
    }
  } catch {
    // Continue to public fallbacks.
  }
  for (const endpoint of RSS_FALLBACK_ENDPOINTS) {
    const fallbackUrl = `${endpoint}${encodeURIComponent(username)}`;
    try {
      const fallbackRes = await fetch(fallbackUrl, { signal });
      if (!fallbackRes.ok) continue;
      const rawXml = await fallbackRes.text();
      const items = parseRssFallback(normalizeRssXml(rawXml));
      if (items.length) return items;
    } catch {
      // Try next fallback.
    }
  }
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) {
      throw new Error(`Failed to fetch Medium feed (${res.status})`);
    }
    const data = (await res.json()) as Rss2JsonResponse;
    if (data.status !== "ok") {
      return [];
    }
    return data.items.map((item) => {
      const thumbnail = extractFirstImage(item.content, item.thumbnail);
      return {
        id: item.guid || item.link,
        title: item.title,
        link: item.link,
        author: item.author,
        categories: item.categories || [],
        pubDate: item.pubDate,
        thumbnail,
        description: stripHtml(item.content || item.description || ""),
      };
    });
  } catch (error) {
    throw error instanceof Error ? error : new Error("Failed to fetch Medium feed");
  }
}
